import React, { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Building2, Plus, Users } from "lucide-react";
import { useData } from "../context/DataContext";
import { useSearch, matchesQuery } from "../context/SearchContext";
import { formatKES, computeTenantStatus, currentMonth } from "../utils/format";
import TenantsTable from "../components/TenantsTable";
import TenantEditor from "../components/TenantEditor";
import HistoryModal from "../components/HistoryModal";
import Select from "../components/Select";
import BulkActionsBar from "../components/BulkActionsBar";
import { ExportTenantsButton, ImportTenantsButton } from "../components/TenantImportExport";

const FILTER_OPTIONS = [
  { value: "ACTIVE", label: "Active" },
  { value: "VACATED", label: "Vacated" },
  { value: "ALL", label: "All" },
];

const STATUS_OPTIONS = [
  { value: "all", label: "All statuses" },
  { value: "paid", label: "Paid" },
  { value: "partial", label: "Partial" },
  { value: "pending", label: "Pending" },
  { value: "overdue", label: "Overdue" },
];

export default function PropertyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { properties, tenants, payments } = useData();
  const { query } = useSearch();
  const property = properties.find((p) => p.id === id);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState("ACTIVE");
  const [statusFilter, setStatusFilter] = useState("all");
  const [history, setHistory] = useState(null);

  if (!property) {
    return (
      <div className="mx-auto max-w-3xl space-y-4">
        <Link
          to="/app/properties"
          className="inline-flex items-center gap-1 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
        >
          <ArrowLeft className="h-4 w-4" /> Back to properties
        </Link>
        <div className="card p-10 text-center text-sm text-gray-500 dark:text-gray-400">
          Property not found.
        </div>
      </div>
    );
  }

  const month = currentMonth();
  const rows = useMemo(() => {
    return tenants
      .filter((t) => t.propertyId === property.id)
      .filter((t) => (filter === "ALL" ? true : t.status === filter))
      .filter((t) => {
        if (statusFilter === "all") return true;
        return computeTenantStatus(t, payments, month).toLowerCase() === statusFilter;
      })
      .filter((t) => matchesQuery(query, t.name, t.phone, t.houseNumber));
  }, [tenants, payments, month, property?.id, filter, statusFilter, query]);

  const active = tenants.filter(
    (t) => t.propertyId === property.id && t.status === "ACTIVE"
  );
  const monthly = active.reduce((a, t) => a + (Number(t.rent) || 0), 0);

  // Sum of all payments (rent + utilities) applied to the current month
  // for tenants in this property. Matches how Dashboard computes revenue.
  const propertyTenantIds = useMemo(
    () => new Set(tenants.filter((t) => t.propertyId === property.id).map((t) => t.id)),
    [tenants, property?.id]
  );
  const collectedThisMonth = useMemo(() => {
    return payments
      .filter((p) => p.month === month && propertyTenantIds.has(p.tenantId))
      .reduce((a, p) => a + (Number(p.amount) || 0), 0);
  }, [payments, month, propertyTenantIds]);

  const openAdd = () => {
    setEditing(null);
    setEditorOpen(true);
  };
  const openEdit = (t) => {
    setEditing(t);
    setEditorOpen(true);
  };

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <button
        onClick={() => navigate("/app/properties")}
        className="inline-flex items-center gap-1 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
      >
        <ArrowLeft className="h-4 w-4" /> Back to properties
      </button>

      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <span className="flex h-12 w-12 flex-none items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
            <Building2 className="h-6 w-6" />
          </span>
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">{property.name}</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">{property.address || "No address"}</p>
          </div>
        </div>
        <button className="btn-primary flex-none" onClick={openAdd}>
          <Plus className="h-4 w-4" />
          Add tenant
        </button>
      </div>

      <div className="flex items-center gap-2">
        <ExportTenantsButton propertyId={property.id} />
        <ImportTenantsButton propertyId={property.id} />
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Active tenants" value={active.length} />
        <StatCard label="Monthly rent" value={formatKES(monthly)} />
        <StatCard
          label="Collected this month"
          value={formatKES(collectedThisMonth)}
          subtitle={monthly > 0 ? `of ${formatKES(monthly)} expected` : null}
          tone="success"
        />
        <StatCard label="Total records" value={tenants.filter((t) => t.propertyId === property.id).length} />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Tenants</h3>
          <span className="rounded-full bg-gray-100 dark:bg-gray-700 px-2 py-0.5 text-xs font-medium text-gray-700 dark:text-gray-300">
            {rows.length} {rows.length === 1 ? "tenant" : "tenants"}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Select
            className="w-36"
            value={filter}
            onChange={setFilter}
            options={FILTER_OPTIONS}
          />
          <Select
            className="w-36"
            value={statusFilter}
            onChange={setStatusFilter}
            options={STATUS_OPTIONS}
          />
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="card p-10 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-indigo-50 text-indigo-600">
            <Users className="h-6 w-6" />
          </div>
          <h3 className="mt-4 text-base font-semibold text-gray-900 dark:text-white">No tenants match</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Try a different filter or add a new tenant.</p>
          <button className="btn-primary mt-5" onClick={openAdd}>
            <Plus className="h-4 w-4" /> Add tenant
          </button>
        </div>
      ) : (
        <>
          <BulkActionsBar tenants={rows} />
          <TenantsTable
            tenants={rows}
            showProperty={false}
            onEdit={openEdit}
            onOpenHistory={setHistory}
          />
        </>
      )}

      <TenantEditor
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        editingTenant={editing}
        defaultPropertyId={property.id}
        lockedPropertyId={property.id}
      />

      <HistoryModal
        open={!!history}
        onClose={() => setHistory(null)}
        scope={history?.scope}
        tenant={history?.tenant}
      />
    </div>
  );
}

function StatCard({ label, value, subtitle, tone }) {
  const valueClass =
    tone === "success"
      ? "text-green-600 dark:text-green-400"
      : "text-gray-900 dark:text-white";
  return (
    <div className="card p-5">
      <div className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</div>
      <div className={`mt-2 text-2xl font-semibold ${valueClass}`}>{value}</div>
      {subtitle && (
        <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{subtitle}</div>
      )}
    </div>
  );
}
